<template>
  <div class="judge-card" :class="statusClass(judge.status_id)" @click.prevent="$emit('viewJudge', judge.user.id)">
    <!-- 🔹 Header con nombre truncado -->
    <div class="judge-header">
      <h4 class="judge-name">{{ judge.user.name }} {{ judge.user.surname }}</h4>
      <button class="close-button" v-if="judge.status_id === 1 && isOwnerOrganizer(user, competition) && !competition.started_at" @click.stop="modalDelete(judge.id)">×</button>
    </div>

    <!-- 🔸 Cuerpo con imagen e info -->
    <div class="judge-body">
      <!-- Imagen -->
      <img :src="judge.user.img_path || defaultAvatar" alt="Judge Image" class="judge-image" />

      <!-- Info a la derecha -->
      <div class="judge-info">
        <p class="judge-status">
          <i class="fa-solid fa-check-circle"
             :class="{ 'approved': judge.status_id === 4, 'pending': judge.status_id !== 4 }"></i>
          {{ judge.status.name }}
        </p>
        <p v-if="isLiveCompetition && isTechQualified" class="judge-cert">
          <i class="fa-solid fa-video tech-icon" aria-hidden="true"></i>
          <span>{{ techStatusText }}</span>
        </p>
      </div>
    </div>

    <!-- 🔻 Footer con botones -->
    <div class="judge-footer" @click.stop>
      <button class="action-btn confirm"
              @click.stop="modalConfirm(judge.id)"
              v-if="judge.status_id === 2 && isOwnerAthlete(user, judge) && !competition.started_at">
        <i class="fa-solid fa-thumbs-up"></i>
      </button>

      <button class="action-btn reject"
              @click.stop="modalReject(judge.id)"
              v-if="judge.status_id !== 1 && (isOwnerAthlete(user, judge) || isOwnerOrganizer(user, competition)) && !competition.started_at">
        <i class="fa-solid fa-user-slash"></i>
      </button>

      <button
        class="action-btn mail"
        v-if="judge.status_id === 1 && isOwnerOrganizer(user, competition) && !competition.started_at"
        @click.stop="modalSendEmail(judge.id)"
        :title="$t('judge.send_mails_judges')"
      >
        <i class="fa-solid fa-mail-bulk"></i>
      </button>

      <button
        v-if="canManageTechOperator && isTechQualified && isLiveCompetition && isApproved"
        class="action-btn tech-toggle"
        :disabled="isTechToggleDisabled"
        @click.stop="toggleTechOperator"
      >
        <i :class="isTechActive ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off'"></i>
        {{ techToggleLabel }}
      </button>

      <button class="action-btn approve"
              @click.stop="modalApprove(judge.id)"
              v-if="judge.status_id === 3 && isOwnerOrganizer(user, competition) && !competition.started_at">
        <i class="fa-solid fa-check"></i>
      </button>
    </div>
  </div>
</template>


<script>
import { isOwnerAthlete, isOwnerOrganizer } from '@/helpers/roleUtils';
import { mapState } from 'vuex';

export default {
  name: "JudgeCard",
  props: {
    judge: {
      type: Object,
      required: true,
    },
    competition: {
      type: Object,
      required: true,
    },
    activeTechOperatorId: {
      type: [Number, String],
      default: null,
    }
  },
  data() {
    return {
      defaultAvatar: require("@/assets/user.png"),
    };
  },
  computed: {
    ...mapState('user', ['user']),
    isLiveCompetition() {
      return Number(this.competition?.type_id) === 2;
    },
    isTechQualified() {
      const flag = this.judge?.tech_operator_qualified;
      return flag === true || flag === 'true' || Number(flag) === 1;
    },
    isTechActive() {
      const flag = this.judge?.tech_operator_active;
      return flag === true || flag === 'true' || Number(flag) === 1;
    },
    isApproved() {
      return Number(this.judge?.status_id) === 4;
    },
    techStatusText() {
      return this.isTechActive
        ? this.$t('judge.tech_operator.active')
        : this.$t('judge.tech_operator.available');
    },
    techToggleLabel() {
      return this.isTechActive
        ? this.$t('judge.tech_operator.btn_deactivate')
        : this.$t('judge.tech_operator.btn_activate');
    },
    isTechToggleDisabled() {
      if (!this.isApproved) return true;
      if (this.competition?.started_at) return true;
      if (!this.activeTechOperatorId) return false;
      return Number(this.activeTechOperatorId) !== Number(this.judge?.id) && !this.isTechActive;
    },
    canManageTechOperator() {
      // Solo el organizador creador y antes de iniciar la competencia
      if (this.competition?.started_at) return false;
      return this.isLiveCompetition && isOwnerOrganizer(this.user, this.competition);
    },
  },
  methods: {
    isOwnerAthlete,
    isOwnerOrganizer,
    statusClass(status_id) {
      return {
        "status-invitation-sent": status_id === 2,
        "status-pending-approval": status_id === 3,
        "status-approved": status_id === 4,
        "status-declined": status_id === 5,
        "status-competing": status_id === 18

      };
    },
    modalDelete(id) {
      this.$emit('update-show-modal', 'deleteJudge',id,this.$t('judge.modal.delete_confirmation')); // 🔥 Emití solo si el usuario confirma
    },
    modalReject(id) {
        this.$emit('update-show-modal', 'rejectJudge',id,this.$t('judge.modal.decline_confirmation')); // 🔥 Emití solo si el usuario confirma
    },
    modalConfirm(id) {
        this.$emit('update-show-modal', 'confirmJudge',id,this.$t('judge.modal.confirm_confirmation')); // 🔥 Emití solo si el usuario confirma
    },
    modalApprove(id) {
        this.$emit('update-show-modal', 'approveJudge',id,this.$t('judge.modal.approve_confirmation')); // 🔥 Emití solo si el usuario confirma
    },
    modalSendEmail(id) {
      this.$emit('update-show-modal', 'sendEmailJudge', id, this.$t('judge.modal.send_invitation_confirmation'));
    },
    toggleTechOperator() {
      if (!this.isTechQualified || !this.canManageTechOperator) return;
      this.$emit('toggle-tech-operator', {
        competitionUserId: this.judge?.id,
        active: !this.isTechActive,
      });
    },

  }
};
</script>


<style scoped>
.judge-card {
  background-color: white;
  border-radius: 10px;
  padding: 0;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  cursor: pointer;
}

.judge-card:hover {
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
}

/* 🔹 Header */
.judge-header {
  position: relative;
  background-color: #f7f7f7;
  padding: 10px 40px; /* espacio para la X */
  text-align: center;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}

.judge-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.close-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.5em;
  color: #c00;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-weight: bold;
  transition: color 0.2s ease;
}

.close-button:hover {
  color: #900;
}
/* 🔸 Cuerpo con imagen e info */
.judge-body {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
}

.judge-image {
  width: 65px;
  height: 65px;
  border-radius: 50%;
  object-fit: cover;
}

.judge-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.judge-status {
  font-size: 14px;
  margin: 4px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.judge-cert {
  margin: 0;
  font-size: 12px;
  color: #495057;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tech-icon {
  color: #1b5bbf;
  font-size: 0.9em;
}


/* 🔻 Footer con acciones */
.judge-footer {
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding:0;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 6px;
  transition: opacity 0.2s ease;
}

.action-btn.confirm { color: #28a745; }
.action-btn.reject { color: #dc3545; }
.action-btn.mail { color: #c08b2f; }
.action-btn.payment { color: #ffc107; }
.action-btn.approve { color: #007bff; }
.action-btn.tech-toggle {
  color: #1b5bbf;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.action-btn.tech-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn:hover {
  opacity: 0.75;
}

/* 🔸 Status con sombra */
.status-invitation-sent    { box-shadow: 0 0 3px 1px rgba(76, 86, 175, 0.5); }
.status-pending-approval   { box-shadow: 0 0 3px 1px rgba(77, 222, 255, 0.5); }
.status-approved           { box-shadow: 0 0 3px 1px rgba(101, 175, 76, 0.45); }
.status-declined           { box-shadow: 0 0 3px 1px rgba(125, 80, 189, 0.4); }
.status-competing          { box-shadow: 0 0 3px 1px rgba(162, 162, 162, 0.5); }

@media (max-width: 576px) {
  .judge-body {
    display: flex;
    flex-direction: column; /* ⬅️ Cambiamos a columna */
    align-items: center;     /* ⬅️ Centramos en horizontal */
    gap: 8px;
    padding: 12px;
    text-align: center;      /* Asegura que el texto también esté centrado */
  }

  .judge-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
  }

  .judge-info {
    display: flex;
    flex-direction: column;
    align-items: center;     /* ⬅️ Centramos el contenido */
    justify-content: center;
  }

  .judge-status {
    font-size: 14px;
    margin-top: 4px;
  }
}



</style>
